import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import {
  Grid,
  Checkbox,
  TextField,
  Typography,
  Chip,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import moment from "moment-timezone";
import SingleSelect from "components/Select/SingleSelect";
import { setDateRangePicTreeNttFt, setDateRangePicTreeMinMax, setSelectedSeasonNttFt, getSeasonDataNttFt } from "actions";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const useStyles = makeStyles((theme) =>
  createStyles({
    calendarWrapper: {
      paddingLeft: 8,
    },
    root: {
      backgroundColor: "#fafafa",
      zIndex: 1000,
      paddingTop: 16,
      paddingLeft: 8,
      marginTop: 0,
    },
    chip: {
      margin: theme.spacing(0.5),
      fontSize: "0.75rem",
      height: 24,
    },
    autocomplete: {
      width: 510,
      marginTop: -8,
    },
    seasonTextField: {
      "& .MuiOutlinedInput-root": {
        padding: "1px 8px",
      },
      "& .MuiOutlinedInput-input": {
        height: "1.2em",
      },
    },
    singleSeasonWrapper: {
      width: "auto",
      display: "inline-block",
      marginTop: -24,
    },
  })
);

const PeriodNttFt = ({
  setDateRangePicTreeNttFt,
  setDateRangePicTreeMinMax,
  setSelectedSeasonNttFt,
  getSeasonDataNttFt,
  seasonData,
  selectedSeason,
  dateRangePicTreeNttFt,
  singlePeriod,
}) => {
  const classes = useStyles();
  const currentDateTime = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
  const today = moment().tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
  const todayFormatted = moment(today).format("YYYY-MM-DD");
  const normalizeDate = (value) => moment(value).format("YYYY-MM-DD");
  const getClampedRange = (startValue, endValue) => {
    const seasonStart = normalizeDate(startValue);
    const seasonEnd = normalizeDate(endValue);
    if (moment(todayFormatted).isBefore(seasonStart)) {
      return [todayFormatted, todayFormatted];
    }
    if (moment(todayFormatted).isAfter(seasonEnd)) {
      return [seasonStart, seasonEnd];
    }
    return [seasonStart, todayFormatted];
  };

  const [seasonOption, setSeasonOption] = useState(
    { name: "沖縄シーズン", value: "cmkdxxnxn000141s4asb2gdfv" },
  );

  useEffect(() => {
    getSeasonDataNttFt();
  }, []);

  useEffect(() => {
    if (seasonData.length > 0) {
      let matchedSeasons = seasonData.filter((season) => {
        const startDate = season.seasonStart;
        const endDate = season.seasonEnd;
        return today >= startDate && today <= endDate;
      });
      if (matchedSeasons.length > 0) {
        matchedSeasons = matchedSeasons[0];
      }
      if (!selectedSeason || selectedSeason.length === 0) {
        if (matchedSeasons.length > 0) {
          setSelectedSeasonNttFt(matchedSeasons);
          const earliestStart = matchedSeasons
            .map((s) => s.seasonStart)
            .reduce((a, b) => (a < b ? a : b));
          const latestEnd = matchedSeasons
            .map((s) => s.seasonEnd)
            .reduce((a, b) => (a > b ? a : b));

          const [rangeStart, rangeEnd] = getClampedRange(
            earliestStart,
            latestEnd
          );
          setDateRangePicTreeMinMax([rangeStart, rangeEnd]);
          setDateRangePicTreeNttFt([rangeStart, rangeEnd]);
        } else {
          setSelectedSeasonNttFt([seasonData[0]]);
          const [rangeStart, rangeEnd] = getClampedRange(
            seasonData[0]["seasonStart"],
            seasonData[0]["seasonEnd"]
          );
          setDateRangePicTreeNttFt([rangeStart, rangeEnd]);
          setDateRangePicTreeMinMax([rangeStart, rangeEnd]);
        }
      }
    }
  }, [seasonData]);

  const handleSelectionChange = (event, value) => {
    const uniqueValue = value.filter((option, index, self) => {
      const isDuplicate =
        self.filter((item) => item.seasonId === option.seasonId).length > 1;
      return !isDuplicate;
    });

    const validSelections = uniqueValue.filter(
      (option, index, self) =>
        self.findIndex((o) => o.seasonId === option.seasonId) === index
    );

    if (validSelections.length > 0) {
      const selectedSeasonsData = seasonData.filter((s) =>
        validSelections.some((item) => item.seasonId === s.seasonId)
      );

      const earliestStart = selectedSeasonsData
        .map((s) => s.seasonStart)
        .reduce((a, b) => (a < b ? a : b));

      const latestEnd = selectedSeasonsData
        .map((s) => s.seasonEnd)
        .reduce((a, b) => (a > b ? a : b));

      const [rangeStart, rangeEnd] = getClampedRange(
        earliestStart,
        latestEnd
      );
      setDateRangePicTreeMinMax([rangeStart, rangeEnd]);
      setDateRangePicTreeNttFt([rangeStart, rangeEnd]);

      // ステートを更新
      setSelectedSeasonNttFt(validSelections);
    } else {
      setDateRangePicTreeMinMax([]);
      setDateRangePicTreeNttFt([]);
    }
  };

  const handleChange = (event) => {
    setSeasonOption(event.target);
    if (event.target.value === "cmkdxxnxn000141s4asb2gdfv") { // okinawa
      setSelectedSeasonNttFt([seasonData[0]]);
      const [rangeStart, rangeEnd] = getClampedRange(
        seasonData[0]["seasonStart"],
        seasonData[0]["seasonEnd"]
      );
      setDateRangePicTreeNttFt([rangeStart, rangeEnd]);
      setDateRangePicTreeMinMax([rangeStart, rangeEnd]);
    }
  };

  return (
    <div className={classes.calendarWrapper}>
      <div className={classes.root}>
        <Grid container spacing={1} direction="row">
          <Grid item spacing={1} style={{marginTop: 8}}>
            シーズン：
          </Grid>
          <Grid item>
            {singlePeriod && (
              <div className={classes.singleSeasonWrapper}>
                <SingleSelect
                  selected={seasonOption}
                  options={[
                    { name: "沖縄シーズン", value: "cmkdxxnxn000141s4asb2gdfv" },
                  ]}
                  handleChange={handleChange}
                />
              </div>
            )}
            {!singlePeriod &&
            <Autocomplete
              multiple
              id="season-autocomplete"
              options={seasonData}
              disableCloseOnSelect
              getOptionLabel={(option) =>
                `${option.seasonName} : ${option.seasonStart} ~ ${option.seasonEnd}`
              }
              renderOption={(option, { selected }) => {
                const isFutureSeason = moment(option.seasonStart).isAfter(currentDateTime);
                const isSelected = selectedSeason.some((selected) => selected.seasonId === option.seasonId);
                return (
                  <div>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={isSelected}
                      disabled={isFutureSeason}
                    />
                    {`${option.seasonName}: `}
                    <span
                      style={{ color: "gray", marginLeft: 16, fontSize: 13 }}
                    >
                      {`${option.seasonStart} ~ ${option.seasonEnd}`}
                    </span>
                  </div>
                );
              }}
              value={selectedSeason || []}
              onChange={handleSelectionChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder=""
                  className={classes.seasonTextField}
                />
              )}
              renderTags={(selected, getTagProps) => {
                const visibleTags = selected.slice(0, 3);
                const extraCount = selected.length - visibleTags.length;
                return [
                  ...visibleTags.map((option, index) => (
                    <Chip
                      key={index}
                      label={option.seasonName}
                      size="small"
                      className={classes.chip}
                    />
                  )),
                  extraCount > 0 && (
                    <Typography
                      key="extra"
                      variant="body2"
                      style={{ marginLeft: 8, color: "gray" }}
                    >
                      +{extraCount}
                    </Typography>
                  ),
                ];
              }}
              disableClearable
              className={classes.autocomplete}
            />
            }
          </Grid>
        </Grid>
      </div>
    </div>
  );
};

PeriodNttFt.prototypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  periodType: state.pictree.periodType,
  seasonData: state.pictreeNttFt.seasonData,
  selectedSeason: state.pictreeNttFt.selectedSeason,
  dateRangePicTreeNttFt: state.page.dateRangePicTreeNttFt,
});

const mapDispatchToProps = (dispatch) => ({
  setDateRangePicTreeNttFt: (param) => dispatch(setDateRangePicTreeNttFt(param)),
  setDateRangePicTreeMinMax: (param) => dispatch(setDateRangePicTreeMinMax(param)),
  getSeasonDataNttFt: () => dispatch(getSeasonDataNttFt()),
  setSelectedSeasonNttFt: (param) => dispatch(setSelectedSeasonNttFt(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(PeriodNttFt));
